import { zodResolver } from '@hookform/resolvers/zod';
import React, { useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import i18next from 'i18next';
import useSubmitLead from '../../../Hooks/useSubmitLead';
import { MarketCode } from '../../../@types/MarketCode';
import { getMarketCode } from '../../../utils/market';
import useSettings from '../State/Settings/useSettings';
import { ButtonContent, ButtonPrimary, ButtonSecondary } from '../../../StyledComponents/Button';
import InputCheckbox from '../../../StyledComponents/InputCheckbox';
import InputError from '../../../StyledComponents/InputError';
import InputLabel from '../../../StyledComponents/InputLabel';
import InputText from '../../../StyledComponents/InputText';
import Modal from '../../../StyledComponents/Modal';
import { InputTextItem } from '../../Search/FilterPanel/RangeSlider/wrapper';
import { Repeat, RepeatTiny } from '../../../StyledComponents/Repeat';


interface LeadFormProps {
  itemId?: string;
  branchId?: string;
}

// Swedish plates: three letters, two digits and a final digit or letter (I, Q and V are unused).
// Norwegian plates: two letters followed by five digits.
const registrationNumberPatterns: Partial<Record<MarketCode, RegExp>> = {
  SE: /^[A-HJ-PR-UW-Z]{3}[0-9]{2}[0-9A-HJ-PR-UW-Z]$/,
  NO: /^[A-Z]{2}[0-9]{5}$/,
};

export const normalizeRegistrationNumber = (value: string) =>
  value.replace(/[\s-]/g, '').toUpperCase();

const isValidRegistrationNumber = (value: string, marketCode: MarketCode) => {
  const pattern = registrationNumberPatterns[marketCode];
  return !pattern || pattern.test(normalizeRegistrationNumber(value));
};

const createSchema = (marketCode: MarketCode) =>
  z
    .object({
      communication: z.enum(['email', 'callme']).optional(),
      firstName: z.string().min(1, i18next.t('lead.validationMessages.requiredFirstName')),
      lastName: z.string().min(1, i18next.t('lead.validationMessages.requiredLastName')),
      email: z.email(i18next.t('lead.validationMessages.invalidEmail')).or(z.literal('')).optional(),
      phoneNumber: z.string().optional(),
      message: z.string().max(500, i18next.t('lead.validationMessages.maxMessage')),
      tradeInCar: z.boolean(),
      tradeInCarRegNo: z.string().optional(),
      tradeInCarMileage: z.string().optional(),
    })
    .refine(
      (data) => !data.tradeInCar || (data.tradeInCarRegNo && data.tradeInCarRegNo.trim() !== ''),
      {
        message: i18next.t('lead.validationMessages.requiredRegNo'),
        path: ['tradeInCarRegNo'],
      },
    )
    .refine(
      (data) =>
        !data.tradeInCar ||
        !data.tradeInCarRegNo ||
        data.tradeInCarRegNo.trim() === '' ||
        isValidRegistrationNumber(data.tradeInCarRegNo, marketCode),
      {
        message: i18next.t('lead.validationMessages.invalidRegNo'),
        path: ['tradeInCarRegNo'],
      },
    )
    .refine(
      (data) => !data.tradeInCar || (data.tradeInCarMileage && data.tradeInCarMileage.trim() !== ''),
      {
        message: i18next.t('lead.validationMessages.requiredMileage'),
        path: ['tradeInCarMileage'],
      },
    ).refine(
      (data) =>
        data.communication !== 'callme' || (data.phoneNumber && data.phoneNumber.trim() !== ''),
      {
        message: i18next.t('lead.validationMessages.requiredPhoneNumber'),
        path: ['phoneNumber'],
      },
    ).refine(
      (data) =>
        data.communication !== 'email' || (data.email && data.email.trim() !== ''),
      {
        message: i18next.t('lead.validationMessages.requiredEmail'),
        path: ['email'],
      },
    );

export const LeadForm = ({ branchId, itemId }: LeadFormProps) => {
  const settings = useSettings();
  const marketCode = getMarketCode();
  const schema = useMemo(() => createSchema(marketCode), [marketCode]);
  const { submitLead, isLoading } = useSubmitLead(settings.apiUrl || '');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const formId = useId();
  const firstNameId = `${formId}-firstName`;
  const lastNameId = `${formId}-lastName`;
  const emailId = `${formId}-email`;
  const phoneNumberId = `${formId}-phoneNumber`;
  const messageId = `${formId}-message`;
  const tradeInCarRegNoId = `${formId}-tradeInCarRegNo`;
  const tradeInCarMileageId = `${formId}-tradeInCarMileage`;
  const { handleSubmit, formState: { errors }, watch, control, setValue, reset
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      tradeInCar: false,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      message: '',
      tradeInCarRegNo: '',
      tradeInCarMileage: '',
      communication: undefined,
    },
  });
  const communication = watch('communication');
  const tradeInCar = watch('tradeInCar');

  const onSubmitForm = handleSubmit((data) => {
    const { communication, firstName, lastName, email, message, tradeInCar, tradeInCarRegNo, tradeInCarMileage, phoneNumber } = data;

    if (!branchId || !communication || !itemId) {
      return;
    }

    submitLead({
      communication,
      branchId,
      itemId,
      phoneNumber,
      firstName,
      lastName,
      email,
      message,
      tradeInCarMileage: tradeInCar ? tradeInCarMileage : undefined,
      tradeInCarRegNo:
        tradeInCar && tradeInCarRegNo ? normalizeRegistrationNumber(tradeInCarRegNo) : undefined,
    }).then(() => {
      reset();
      setShowConfirmation(true);
    })
  }, (error) => console.error(error));

  return (
    <>
      <RepeatTiny>
        <ButtonPrimary fullWidth onClick={() => setValue("communication", 'email')}>
          <ButtonContent>
            {i18next.t('lead.messageFormTitle')}
          </ButtonContent>
        </ButtonPrimary>
      </RepeatTiny>
      <RepeatTiny>
        <ButtonPrimary fullWidth onClick={() => setValue("communication", 'callme')}>
          <ButtonContent>
            {i18next.t('lead.callFormTitle')}
          </ButtonContent>
        </ButtonPrimary>
      </RepeatTiny>
      {communication && (
        <Modal
          title={communication === 'email'
            ? i18next.t('lead.messageFormTitle')
            : i18next.t('lead.callFormTitle')}
          onClose={() => {
            reset();
            setValue("communication", undefined);
            setShowConfirmation(false);
          }}
        >
          <form onSubmit={onSubmitForm}>
            <Repeat>
              <RepeatTiny>
                <InputTextItem>
                  <InputLabel htmlFor={firstNameId}>{i18next.t('lead.firstName')}</InputLabel>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id={firstNameId}
                        label={i18next.t('lead.firstName')}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder=""
                        aria-required={true}
                        aria-describedby={errors.firstName ? `${firstNameId}-error` : undefined}
                      />
                    )}
                  />
                  {errors.firstName && <InputError id={`${firstNameId}-error`}>{errors.firstName.message}</InputError>}
                </InputTextItem>
              </RepeatTiny>
              <RepeatTiny>
                <InputTextItem>
                  <InputLabel htmlFor={lastNameId}>{i18next.t('lead.lastName')}</InputLabel>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id={lastNameId}
                        label={i18next.t('lead.lastName')}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder=""
                        aria-required={true}
                        aria-describedby={errors.lastName ? `${lastNameId}-error` : undefined}
                      />
                    )}
                  />
                  {errors.lastName && <InputError id={`${lastNameId}-error`}>{errors.lastName.message}</InputError>}
                </InputTextItem>
              </RepeatTiny>
              {communication === "email" && (<RepeatTiny>
                <InputTextItem>
                  <InputLabel htmlFor={emailId}>{i18next.t('lead.email')}</InputLabel>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id={emailId}
                        label={i18next.t('lead.email')}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder=""
                        aria-required={true}
                        aria-describedby={errors.email ? `${emailId}-error` : undefined}
                      />
                    )}
                  />
                  {errors.email && <InputError id={`${emailId}-error`}>{errors.email.message}</InputError>}
                </InputTextItem>
              </RepeatTiny>)}
              {communication === "callme" && (<RepeatTiny>
                <InputTextItem>
                  <InputLabel htmlFor={phoneNumberId}>{i18next.t('lead.phoneNumber')}</InputLabel>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id={phoneNumberId}
                        label={i18next.t('lead.phoneNumber')}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder=""
                        aria-required={true}
                        aria-describedby={errors.phoneNumber ? `${phoneNumberId}-error` : undefined}
                      />
                    )}
                  />
                  {errors.phoneNumber && <InputError id={`${phoneNumberId}-error`}>{errors.phoneNumber.message}</InputError>}
                </InputTextItem>
              </RepeatTiny>)}
              <RepeatTiny>
                <InputTextItem>
                  <InputLabel htmlFor={messageId}>{i18next.t('lead.message')}</InputLabel>
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id={messageId}
                        label={i18next.t('lead.message')}
                        placeholder=""
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        aria-describedby={errors.message ? `${messageId}-error` : undefined}
                      />
                    )}
                  />
                  {errors.message && <InputError id={`${messageId}-error`}>{errors.message.message}</InputError>}
                </InputTextItem>
              </RepeatTiny>
              <RepeatTiny>
                <InputTextItem>
                  <Controller
                    name="tradeInCar"
                    control={control}
                    render={({ field }) => (
                      <InputCheckbox
                        id="input-tradein-car"
                        checked={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      >
                        {i18next.t('lead.tradeInCar')}
                      </InputCheckbox>
                    )}
                  />
                </InputTextItem>
              </RepeatTiny>
              {tradeInCar && (
                <>
                  <RepeatTiny>
                    <InputTextItem>
                      <InputLabel htmlFor={tradeInCarRegNoId}>{i18next.t('lead.registrationNumber')}</InputLabel>
                      <Controller
                        name="tradeInCarRegNo"
                        control={control}
                        render={({ field }) => (
                          <InputText
                            id={tradeInCarRegNoId}
                            label={i18next.t('lead.registrationNumber')}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder=""
                            aria-required={true}
                            aria-describedby={errors.tradeInCarRegNo ? `${tradeInCarRegNoId}-error` : undefined}
                          />
                        )}
                      />
                      {errors.tradeInCarRegNo && <InputError id={`${tradeInCarRegNoId}-error`}>{errors.tradeInCarRegNo.message}</InputError>}
                    </InputTextItem>
                  </RepeatTiny>
                  <RepeatTiny>
                    <InputTextItem>
                      <InputLabel htmlFor={tradeInCarMileageId}>{i18next.t('lead.mileage')}</InputLabel>
                      <Controller
                        name="tradeInCarMileage"
                        control={control}
                        render={({ field }) => (
                          <InputText
                            id={tradeInCarMileageId}
                            label={i18next.t('lead.mileage')}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder=""
                            aria-required={true}
                            aria-describedby={errors.tradeInCarMileage ? `${tradeInCarMileageId}-error` : undefined}
                          />
                        )}
                      />
                      {errors.tradeInCarMileage && <InputError id={`${tradeInCarMileageId}-error`}>{errors.tradeInCarMileage.message}</InputError>}
                    </InputTextItem>
                  </RepeatTiny>
                </>
              )}
              <RepeatTiny>
                <ButtonPrimary onClick={onSubmitForm} disabled={isLoading}>
                  <ButtonContent>
                    {i18next.t('lead.send')}
                  </ButtonContent>
                </ButtonPrimary>
                <ButtonSecondary onClick={() => {
                  reset();
                  setValue("communication", undefined);
                  setShowConfirmation(false);
                }}>
                  <ButtonContent>
                    {i18next.t('lead.close')}
                  </ButtonContent>
                </ButtonSecondary>
              </RepeatTiny>
            </Repeat>
          </form>
        </Modal >
      )}
      {showConfirmation && (
        <Modal
          title={i18next.t('lead.confirmationTitle')}
          onClose={() => setShowConfirmation(false)}
        >
          <Repeat>
            <RepeatTiny>
              <p>{i18next.t('lead.confirmationMessage')}</p>
            </RepeatTiny>
            <RepeatTiny>
              <ButtonPrimary onClick={() => setShowConfirmation(false)}>
                <ButtonContent>
                  {i18next.t('lead.confirmationClose')}
                </ButtonContent>
              </ButtonPrimary>
            </RepeatTiny>
          </Repeat>
        </Modal>
      )}
    </>
  );
}